import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import { Box, Chip, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro-6";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useDateFnsLocale } from "../../../../../utils/date-locale";
import { Holiday, HolidayScope } from "../../../../holidays/types";
import {
	dateKeyToLocalDate,
	toDateKey,
} from "../../../../holidays/utils/holiday-dates";

const cellSx = {
	fontFamily: "Roboto",
	fontSize: "14px",
	color: "rgba(0, 0, 0, 0.87)",
	padding: "8px 4px",
};

const secondaryCellSx = { ...cellSx, color: "rgba(0, 0, 0, 0.6)" };

const actionIconSx = {
	color: "#6c6c6c",
	cursor: "pointer",
	display: "flex",
	"&:hover": { transform: "scale(1.2)" },
};

interface UseHolidayColumnsProps {
	scope: HolidayScope;
	locationName?: string;
	canManageCompanyHolidays: boolean;
	onEdit: (holiday: Holiday) => void;
	onRemove: (holiday: Holiday) => void;
}

const renderHeader = (label: string) =>
	function HolidayColumnHeader() {
		return (
			<span style={{ padding: "0px 4px", fontWeight: "bold" }}>{label}</span>
		);
	};

const useHolidayColumns = ({
	scope,
	locationName,
	canManageCompanyHolidays,
	onEdit,
	onRemove,
}: UseHolidayColumnsProps): GridColDef[] => {
	const { t } = useTranslation();
	const locale = useDateFnsLocale();

	return useMemo(() => {
		const scopeColumn: GridColDef =
			scope === "company"
				? {
					field: "isActive",
					flex: 1,
					minWidth: 110,
					sortable: false,
					renderHeader: renderHeader(t("holidays_column-status")),
					renderCell: () => (
						<Chip
							size="small"
							label={t("holidays_status-active")}
							sx={{ backgroundColor: "#EDEDED", borderRadius: "8px" }}
						/>
					),
				}
				: {
					field: "locationId",
					flex: 1,
					minWidth: 140,
					renderHeader: renderHeader(t("holidays_column-scope")),
					valueGetter: (params) =>
						params.row.locationId === null
							? t("holidays_scope-company")
							: locationName,
					renderCell: (params: GridRenderCellParams<Holiday>) =>
						params.row.locationId === null ? (
							<Chip
								size="small"
								label={t("holidays_scope-company")}
								sx={{
									backgroundColor: "#EDEDED",
									color: "rgba(0, 0, 0, 0.7)",
									borderRadius: "8px",
								}}
							/>
						) : (
							<Chip
								size="small"
								label={locationName}
								sx={{
									backgroundColor: "#E0EBFF",
									color: "#2F4D8B",
									borderRadius: "8px",
								}}
							/>
						),
				};

		return [
			{
				field: "name",
				flex: 2,
				minWidth: 180,
				renderHeader: renderHeader(t("holidays_column-holiday")),
				renderCell: (params: GridRenderCellParams<Holiday>) => (
					<Box sx={cellSx}>{params.row.name}</Box>
				),
			},
			{
				field: "holidayTypeName",
				flex: 1.5,
				minWidth: 150,
				renderHeader: renderHeader(t("holidays_column-type")),
				renderCell: (params: GridRenderCellParams<Holiday>) => (
					<Box sx={cellSx}>{params.row.holidayTypeName}</Box>
				),
			},
			{
				field: "date",
				flex: 1,
				minWidth: 120,
				renderHeader: renderHeader(t("holidays_column-date")),
				renderCell: (params: GridRenderCellParams<Holiday>) => (
					<Box sx={cellSx}>
						{format(
							dateKeyToLocalDate(toDateKey(params.row.date)),
							"MMM d, yyyy",
							{ locale }
						)}
					</Box>
				),
			},
			{
				field: "isRecurringYearly",
				flex: 1,
				minWidth: 110,
				renderHeader: renderHeader(t("holidays_column-recurrence")),
				renderCell: (params: GridRenderCellParams<Holiday>) => (
					<Box sx={secondaryCellSx}>
						{params.row.isRecurringYearly
							? t("holidays_recurrence-every-year")
							: t("holidays_recurrence-one-time")}
					</Box>
				),
			},
			scopeColumn,
			{
				field: "actions",
				type: "actions",
				flex: 0.8,
				minWidth: 90,
				sortable: false,
				align: "left",
				cellClassName: "hover-column-cell",
				headerClassName: "hover-column-header",
				renderHeader: () => <></>,
				renderCell: (params: GridRenderCellParams<Holiday>) => {
					const holiday = params.row;
					const isCompanyRow = holiday.locationId === null;
					if (isCompanyRow && !canManageCompanyHolidays) {
						return (
							<Tooltip title={t("holidays_managed-at-company")}>
								<LockIcon
									fontSize="small"
									sx={{ color: "rgba(0, 0, 0, 0.38)", ml: "12px" }}
									aria-label={t("holidays_managed-at-company")}
								/>
							</Tooltip>
						);
					}
					return (
						<Box
							className="hover-content"
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								padding: "8px 12px",
							}}
						>
							<Tooltip title={t("common_edit")}>
								<Box
									component="button"
									type="button"
									aria-label={t("holidays_edit-aria", { name: holiday.name })}
									onClick={() => onEdit(holiday)}
									sx={{
										...actionIconSx,
										border: "none",
										background: "none",
										padding: 0,
										"&:hover": { ...actionIconSx["&:hover"], color: "#000000" },
									}}
								>
									<EditIcon fontSize="small" />
								</Box>
							</Tooltip>
							<Tooltip title={t("holidays_remove")}>
								<Box
									component="button"
									type="button"
									aria-label={t("holidays_remove-aria", { name: holiday.name })}
									onClick={() => onRemove(holiday)}
									sx={{
										...actionIconSx,
										border: "none",
										background: "none",
										padding: 0,
										"&:hover": { ...actionIconSx["&:hover"], color: "#E44B48" },
									}}
								>
									<DeleteOutlineIcon fontSize="small" />
								</Box>
							</Tooltip>
						</Box>
					);
				},
			},
		];
	}, [scope, locationName, canManageCompanyHolidays, onEdit, onRemove, t, locale]);
};

export default useHolidayColumns;
